import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle2, Trash2, Users, Building2, Tag, Save, CreditCard } from "lucide-react";

function ClaimList({ accountType }) {
  const [claims, setClaims] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get(`/admin/payment-claims?account_type=${accountType}`).then((r) => setClaims(r.data)).catch(() => setClaims([]));
  useEffect(() => { load(); }, [accountType]);

  const activate = async (id) => {
    setBusy(id);
    try { await api.post(`/admin/payment-claims/${id}/activate`); toast.success("Kontot aktiverat"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const remove = async (id) => {
    if (!window.confirm("Ta bort denna betalningsrad?")) return;
    try { await api.delete(`/admin/payment-claims/${id}`); setClaims((c) => c.filter((x) => x.id !== id)); toast.success("Borttagen"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (claims === null) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-primary" /></div>;
  if (claims.length === 0) return <p className="text-sm text-muted-foreground py-6 text-center">Inga betalningar ännu.</p>;

  return (
    <div className="grid gap-2" data-testid={`claims-${accountType}`}>
      {claims.map((c) => (
        <Card key={c.id} className="rounded-xl border-border p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`claim-${c.id}`}>
          <div className="min-w-0">
            <div className="font-medium text-sm truncate">{c.email}{c.company_name ? ` · ${c.company_name}` : ""}</div>
            <div className="text-xs text-muted-foreground">
              Referens: <span className="font-mono text-foreground">{c.candidate_id}</span>
              {c.phone ? <> · Mobil: <span className="font-mono">{c.phone}</span></> : ""}
              {" "}· {c.plan_name} · {c.amount} kr · {new Date(c.created_at).toLocaleDateString("sv-SE")}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {c.status === "activated" ? (
              <Badge variant="secondary" className="rounded-full bg-primary/10 text-primary">Aktiv</Badge>
            ) : (
              <Button size="sm" className="rounded-full" onClick={() => activate(c.id)} disabled={busy === c.id} data-testid={`activate-${c.id}`}>
                {busy === c.id ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />} Aktivera konto
              </Button>
            )}
            <Button size="sm" variant="ghost" className="rounded-full text-muted-foreground hover:text-destructive" onClick={() => remove(c.id)} data-testid={`del-claim-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        </Card>
      ))}
    </div>
  );
}

function PriceEditor() {
  const [pricing, setPricing] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/pricing").then((r) => setPricing(r.data)).catch(() => {}); }, []);

  const updatePlan = (list, i, field, value) => {
    setPricing((p) => {
      const copy = { ...p, [list]: p[list].map((pl, idx) => idx === i ? { ...pl, [field]: field === "price" || field === "cv_credits" || field === "searches" ? parseInt(value || "0", 10) : value } : pl) };
      return copy;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/pricing", { swish_number: pricing.swish_number, candidate_plans: pricing.candidate_plans, employer_plans: pricing.employer_plans });
      toast.success("Priser sparade");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!pricing) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="price-editor">
      <div>
        <Label>Swish-nummer</Label>
        <Input className="mt-1.5 max-w-xs" value={pricing.swish_number} onChange={(e) => setPricing((p) => ({ ...p, swish_number: e.target.value }))} data-testid="swish-number-input" />
      </div>

      <div>
        <div className="font-head font-semibold mb-2 flex items-center gap-2"><Users className="h-4 w-4 text-primary" /> Priser – Deltagare</div>
        <div className="grid gap-3">
          {pricing.candidate_plans.map((pl, i) => (
            <div key={pl.id} className="grid gap-2 sm:grid-cols-[1fr_100px_100px_1.4fr] items-end rounded-xl border border-border p-3">
              <div><Label className="text-xs">Namn</Label><Input className="mt-1" value={pl.name} onChange={(e) => updatePlan("candidate_plans", i, "name", e.target.value)} /></div>
              <div><Label className="text-xs">Antal CV</Label><Input type="number" className="mt-1" value={pl.cv_credits} onChange={(e) => updatePlan("candidate_plans", i, "cv_credits", e.target.value)} /></div>
              <div><Label className="text-xs">Pris (kr)</Label><Input type="number" className="mt-1" value={pl.price} onChange={(e) => updatePlan("candidate_plans", i, "price", e.target.value)} data-testid={`cand-price-${i}`} /></div>
              <div><Label className="text-xs">Beskrivning</Label><Input className="mt-1" value={pl.description} onChange={(e) => updatePlan("candidate_plans", i, "description", e.target.value)} /></div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="font-head font-semibold mb-2 flex items-center gap-2"><Building2 className="h-4 w-4 text-primary" /> Priser – Företag</div>
        <div className="grid gap-3">
          {pricing.employer_plans.map((pl, i) => (
            <div key={pl.id} className="grid gap-2 sm:grid-cols-[1fr_100px_100px_1.4fr] items-end rounded-xl border border-border p-3">
              <div><Label className="text-xs">Namn</Label><Input className="mt-1" value={pl.name} onChange={(e) => updatePlan("employer_plans", i, "name", e.target.value)} /></div>
              <div><Label className="text-xs">Antal sökningar</Label><Input type="number" className="mt-1" value={pl.searches} onChange={(e) => updatePlan("employer_plans", i, "searches", e.target.value)} /></div>
              <div><Label className="text-xs">Pris (kr)</Label><Input type="number" className="mt-1" value={pl.price} onChange={(e) => updatePlan("employer_plans", i, "price", e.target.value)} data-testid={`emp-price-${i}`} /></div>
              <div><Label className="text-xs">Beskrivning</Label><Input className="mt-1" value={pl.description} onChange={(e) => updatePlan("employer_plans", i, "description", e.target.value)} /></div>
            </div>
          ))}
        </div>
      </div>

      <Button className="rounded-full" onClick={save} disabled={saving} data-testid="save-pricing-btn">
        {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Spara priser
      </Button>
    </div>
  );
}

export default function AdminBetalningar() {
  const [sub, setSub] = useState("candidate");
  const SUBS = [
    { id: "candidate", label: "Deltagare", icon: Users },
    { id: "employer", label: "Företag", icon: Building2 },
    { id: "pricing", label: "Priser", icon: Tag },
  ];
  return (
    <Card className="rounded-2xl border-border p-6" data-testid="admin-betalningar">
      <div className="flex items-center gap-2 font-head font-semibold text-lg mb-1"><CreditCard className="h-5 w-5 text-primary" /> Betalningar</div>
      <p className="text-sm text-muted-foreground mb-4">Alla som klickat "Jag har swishat". Aktivera konton och hantera priser.</p>
      <div className="flex flex-wrap gap-2 mb-5">
        {SUBS.map((s) => {
          const Icon = s.icon;
          return (
            <Button key={s.id} variant={sub === s.id ? "default" : "outline"} size="sm" className="rounded-full" onClick={() => setSub(s.id)} data-testid={`betalning-tab-${s.id}`}>
              <Icon className="h-4 w-4 mr-1.5" /> {s.label}
            </Button>
          );
        })}
      </div>
      {sub === "candidate" && <ClaimList accountType="candidate" />}
      {sub === "employer" && <ClaimList accountType="employer" />}
      {sub === "pricing" && <PriceEditor />}
    </Card>
  );
}
